'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { HashType, FT_PI_ANALYTICS_DEBOUNCE } from '@whitbread-eos/api';
import getConfig from 'next/config';
import { useEffect, useRef } from 'react';
import { decodeIdToken, getAuthCookie, getLoggedInUserInfo } from '../../getters/auth';
import { hashString } from '../../helpers/hashing';
import { useRestQueryRequest, useUserDetails, useFeatureToggle } from '../../hooks';
import { useAuth0User } from '../../hooks/useAuth0User';
import { useAuthToken } from '../../hooks/useAuthToken';
import { isInnBusinessApp } from '../../server/validators';
import { useUserData } from '../../store/UserContext';
import analytics, { configureAnalytics } from './analytics';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
var defaultAnalyticsData = {};
export function AnalyticsProviderComponent(_ref) {
  var _featureToggles$FT_PI, _decodeIdToken$email, _userData$contactDeta7, _userData$contactDeta8, _userData$contactDeta9, _userData$contactDeta0, _userData$contactDeta1;
  var children = _ref.children,
    _ref$initialAnalytics = _ref.initialAnalyticsData,
    initialAnalyticsData = _ref$initialAnalytics === void 0 ? defaultAnalyticsData : _ref$initialAnalytics,
    _ref$isBusinessBooker = _ref.isBusinessBooker,
    isBusinessBooker = _ref$isBusinessBooker === void 0 ? false : _ref$isBusinessBooker;
  var _ref2 = getConfig() || {},
    _ref2$publicRuntimeCo = _ref2.publicRuntimeConfig,
    publicRuntimeConfig = _ref2$publicRuntimeCo === void 0 ? {} : _ref2$publicRuntimeCo;
  var _useUserData = useUserData(),
    isLoggedIn = _useUserData.isLoggedIn;
  var featureToggles = useFeatureToggle();
  var isAnalyticsDebounceEnabled = (_featureToggles$FT_PI = featureToggles === null || featureToggles === void 0 ? void 0 : featureToggles[FT_PI_ANALYTICS_DEBOUNCE]) !== null && _featureToggles$FT_PI !== void 0 ? _featureToggles$FT_PI : false;
  var isInnBusiness;
  var userData = useUserDetails(isBusinessBooker, isLoggedIn);
  if (typeof window !== 'undefined') {
    var _window$location$host, _window;
    defaultAnalyticsData.browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    defaultAnalyticsData.environment = process.env.NODE_ENV === 'development' ? 'development' : 'production';
    isInnBusiness = isInnBusinessApp((_window$location$host = (_window = window) === null || _window === void 0 || (_window = _window.location) === null || _window === void 0 ? void 0 : _window.host) !== null && _window$location$host !== void 0 ? _window$location$host : '');
  }
  var _useAuthToken = useAuthToken(),
    token = _useAuthToken.token,
    isAuth0TokenEnabled = _useAuthToken.isAuth0Enabled;
  var _useAuth0User = useAuth0User(isAuth0TokenEnabled),
    auth0User = _useAuth0User.user;
  var legacyCookie = isLoggedIn ? getAuthCookie() : '';
  var email = isAuth0TokenEnabled && auth0User !== null && auth0User !== void 0 && auth0User.email ? auth0User.email : (_decodeIdToken$email = decodeIdToken(legacyCookie).email) !== null && _decodeIdToken$email !== void 0 ? _decodeIdToken$email : '';
  var _useRestQueryRequest = useRestQueryRequest(['userDetails', token, email], 'GET', "".concat(publicRuntimeConfig.NEXT_PUBLIC_REST_API, "/customers/hotels/").concat(email, "?business=").concat(isBusinessBooker), {
      Authorization: "Bearer ".concat(token)
    }, {
      enabled: isLoggedIn && !!email && !isInnBusiness
    }),
    isSuccess = _useRestQueryRequest.isSuccess,
    isError = _useRestQueryRequest.isError;
  var isUserLoggedSuccess = isInnBusiness ? true : isSuccess;
  var isUserLoggedFailure = isError || !isLoggedIn;
  var _getLoggedInUserInfo = getLoggedInUserInfo(legacyCookie),
    cdhEmployeeId = _getLoggedInUserInfo.cdhEmployeeId,
    cdhCustomerId = _getLoggedInUserInfo.cdhCustomerId;
  var pendingSignInRef = useRef(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.analyticsData = _objectSpread(_objectSpread({}, window.analyticsData), initialAnalyticsData);
    }
  }, [initialAnalyticsData]);
  useEffect(() => {
    configureAnalytics({
      isAnalyticsDebounceEnabled
    });
  }, [isAnalyticsDebounceEnabled]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    var url = new URL(window.location.href);
    if (url.searchParams.get('authSignInSuccess') === '1') {
      var _url$searchParams$get;
      pendingSignInRef.current = {
        authRedirectPageType: (_url$searchParams$get = url.searchParams.get('authRedirectPageType')) !== null && _url$searchParams$get !== void 0 ? _url$searchParams$get : undefined
      };
      url.searchParams.delete('authSignInSuccess');
      url.searchParams.delete('authRedirectPageType');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }, []);
  useEffect(() => {
    var updateAnalyticsAsync = function () {
      var _ref3 = _asyncToGenerator(function* () {
        if (isUserLoggedSuccess) {
          var _userData$contactDeta, _userData$contactDeta2, _userData$contactDeta3, _userData$contactDeta4, _userData$contactDeta5, _userData$contactDeta6;
          var userID = (isBusinessBooker ? cdhEmployeeId : cdhCustomerId) || undefined;
          var _yield$Promise$all = yield Promise.all([userData !== null && userData !== void 0 && (_userData$contactDeta = userData.contactDetail) !== null && _userData$contactDeta !== void 0 && _userData$contactDeta.mobile ? hashString(userData.contactDetail.mobile, HashType.SHA256) : Promise.resolve(undefined), userData !== null && userData !== void 0 && (_userData$contactDeta2 = userData.contactDetail) !== null && _userData$contactDeta2 !== void 0 && _userData$contactDeta2.telephone ? hashString(userData.contactDetail.telephone, HashType.SHA256) : Promise.resolve(undefined), userData !== null && userData !== void 0 && (_userData$contactDeta3 = userData.contactDetail) !== null && _userData$contactDeta3 !== void 0 && _userData$contactDeta3.firstName ? hashString(userData.contactDetail.firstName, HashType.SHA256) : Promise.resolve(undefined), userData !== null && userData !== void 0 && (_userData$contactDeta4 = userData.contactDetail) !== null && _userData$contactDeta4 !== void 0 && _userData$contactDeta4.lastName ? hashString(userData.contactDetail.lastName, HashType.SHA256) : Promise.resolve(undefined)]),
            _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 4),
            userMobile = _yield$Promise$all2[0],
            userTelephone = _yield$Promise$all2[1],
            userFN = _yield$Promise$all2[2],
            userLN = _yield$Promise$all2[3];
          var userCountry = (_userData$contactDeta5 = userData === null || userData === void 0 || (_userData$contactDeta6 = userData.contactDetail) === null || _userData$contactDeta6 === void 0 || (_userData$contactDeta6 = _userData$contactDeta6.address) === null || _userData$contactDeta6 === void 0 ? void 0 : _userData$contactDeta6.countryCode) !== null && _userData$contactDeta5 !== void 0 ? _userData$contactDeta5 : undefined;
          analytics.update({
            userID,
            userHashedEA: yield hashString(email, HashType.SHA256),
            userLoggedIn: 'Logged In',
            userMobile: userMobile,
            userTelephone: userTelephone,
            userFN: userFN,
            userLN: userLN,
            userCountry: userCountry,
            selectedSearch: undefined
          });
          if (pendingSignInRef.current) {
            analytics.track('auth_sign_in_success', {
              authStep: 'success',
              authRedirectPageType: pendingSignInRef.current.authRedirectPageType
            });
            pendingSignInRef.current = null;
          }
        }
        if (isUserLoggedFailure) {
          analytics.update({
            userID: undefined,
            userHashedEA: undefined,
            userLoggedIn: 'Not Logged In',
            userMobile: undefined,
            userTelephone: undefined,
            userFN: undefined,
            userLN: undefined,
            userCountry: undefined,
            selectedSearch: undefined
          });
        }
      });
      return function updateAnalyticsAsync() {
        return _ref3.apply(this, arguments);
      };
    }();
    updateAnalyticsAsync();
  }, [isLoggedIn, isUserLoggedSuccess, isUserLoggedFailure, userData === null || userData === void 0 ? void 0 : userData.customerAccountId, userData === null || userData === void 0 || (_userData$contactDeta7 = userData.contactDetail) === null || _userData$contactDeta7 === void 0 ? void 0 : _userData$contactDeta7.firstName, userData === null || userData === void 0 || (_userData$contactDeta8 = userData.contactDetail) === null || _userData$contactDeta8 === void 0 ? void 0 : _userData$contactDeta8.lastName, userData === null || userData === void 0 || (_userData$contactDeta9 = userData.contactDetail) === null || _userData$contactDeta9 === void 0 || (_userData$contactDeta9 = _userData$contactDeta9.address) === null || _userData$contactDeta9 === void 0 ? void 0 : _userData$contactDeta9.countryCode, userData === null || userData === void 0 || (_userData$contactDeta0 = userData.contactDetail) === null || _userData$contactDeta0 === void 0 ? void 0 : _userData$contactDeta0.mobile, userData === null || userData === void 0 || (_userData$contactDeta1 = userData.contactDetail) === null || _userData$contactDeta1 === void 0 ? void 0 : _userData$contactDeta1.telephone]);
  return _jsx(_Fragment, {
    children: children
  });
}